import { TanStackPostalCodeField } from "@sellar-io/components/adapters";
import { CountryCodeDictionary, CountryType } from "@sellar-io/common";
import { type DeepKeys, useStore } from "@tanstack/react-form";
import { type ReactNode } from "react";
import {
  H4,
  H6,
  InputField,
  InputOption,
  InputSelect,
  InputText,
} from "@sellar-io/components";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { BusinessFormCreateProps } from "./BusinessFormCreate";
import {
  type BusinessFormApi,
  type BusinessFormFieldValues,
  type BusinessFormType,
} from "./BusinessFormValues";
import { BooleanSwitch } from "./BusinessFormFragment";
import {
  fieldNote,
  getFieldError,
  invalidFieldClassName,
  requiredField,
} from "./formValidation";

type BusinessFieldName = DeepKeys<BusinessFormFieldValues>;
const fieldName = (name: string) => name as BusinessFieldName;

export const BusinessAddressFormFragment = ({
  disabled = false,
  form,
  path,
  type,
  metadata,
}: {
  disabled?: boolean;
  form: BusinessFormApi;
  path: string;
  type?: BusinessFormType;
  metadata?: BusinessFormCreateProps["metadata"];
}) => {
  const { business } = useSellarCurrentBusiness();

  const collectionAddressCreationEnabled = useRemoteFeatureFlags().isActive(
    "collectionAddressCreation",
  );
  const isSupplierCreatingCustomer =
    !!business?.Supplier &&
    type === "buyer" &&
    metadata?.createdFrom === "Add customer";

  const isSupplierCreatingNewVenue =
    !!business?.Supplier &&
    type === "buyer" &&
    metadata?.source === "new-venue";
  const isSupplier = !!business?.Supplier;
  const hasCollectionAddressSwitch =
    collectionAddressCreationEnabled && isSupplier && type !== "buyer";

  if (isSupplierCreatingCustomer) {
    return (
      <AddressFormFields
        addressLabel="Business Address"
        addressNote="This is the address for where your customer's business operates, i.e. where their orders should be delivered to"
        invoiceSwitchLabel="Use business address as invoice address"
        invoiceAddressNote="This is the address for where your customer's business or organisation is registered to. This will be displayed in the appropriate places on invoices."
        disabled={disabled}
        form={form}
        path={path}
      />
    );
  }

  if (isSupplier) {
    return (
      <AddressFormFields
        addressLabel={
          isSupplierCreatingNewVenue ? "Delivery Address" : "Business Address"
        }
        addressNote={
          isSupplierCreatingNewVenue
            ? undefined
            : 'This is the address of your place of business such as your production facility. "City, Region" will be displayed on your storefront and on the Sellar market.'
        }
        invoiceSwitchLabel="My invoice address is the same as my delivery address"
        invoiceAddressNote="This is the registered address of your business or organisation. This will be displayed in the appropriate places on invoices."
        hasCollectionAddressSwitch={hasCollectionAddressSwitch}
        disabled={disabled}
        form={form}
        path={path}
      />
    );
  }

  return (
    <AddressFormFields
      addressLabel="Delivery Address"
      invoiceSwitchLabel="My invoice address is the same as my delivery address"
      disabled={disabled}
      form={form}
      path={path}
    />
  );
};

const AddressFormFields = ({
  addressLabel,
  addressNote,
  invoiceSwitchLabel,
  invoiceAddressNote,
  hasCollectionAddressSwitch = false,
  disabled,
  form,
  path,
}: {
  addressLabel: string;
  addressNote?: string;
  invoiceSwitchLabel: string;
  invoiceAddressNote?: string;
  hasCollectionAddressSwitch?: boolean;
  disabled: boolean;
  form: BusinessFormApi;
  path: string;
}) => {
  const isInvoiceAddressSame = useStore(
    form.store,
    (state) => state.values.addresses.isInvoiceAddressSame,
  );
  const isCollectionAddressSame = useStore(
    form.store,
    (state) => state.values.addresses.isCollectionAddressSame,
  );

  return (
    <div className="flex flex-col gap-4">
      <FieldGroup label={addressLabel} note={addressNote}>
        <AddressFields
          disabled={disabled}
          path={`${path}.DeliveryAddress`}
          form={form}
        />
      </FieldGroup>
      <div
        className={`grid gap-3${hasCollectionAddressSwitch ? " md:grid-cols-2" : ""}`}
      >
        <SwitchRow>
          <BooleanSwitch
            disabled={disabled}
            form={form}
            name={`${path}.isInvoiceAddressSame`}
          />
          <div>{invoiceSwitchLabel}</div>
        </SwitchRow>

        {hasCollectionAddressSwitch && (
          <SwitchRow>
            <BooleanSwitch
              disabled={disabled}
              form={form}
              name={`${path}.isCollectionAddressSame`}
            />
            <div>My collection address is the same as my delivery address</div>
          </SwitchRow>
        )}
      </div>

      {!isInvoiceAddressSame && (
        <FieldGroup label={"Invoice Address"} note={invoiceAddressNote}>
          <AddressFields
            disabled={disabled}
            path={`${path}.InvoiceAddress`}
            form={form}
          />
        </FieldGroup>
      )}

      {hasCollectionAddressSwitch && !isCollectionAddressSame && (
        <FieldGroup
          label={"Courier Collection Address"}
          note={
            "This is the address where your stock is located and where third party couriers will collect from. Set this if it differs from your business address."
          }
        >
          <AddressFields
            disabled={disabled}
            path={`${path}.CollectionAddress`}
            form={form}
          />
        </FieldGroup>
      )}
    </div>
  );
};

const AddressFields = ({
  disabled,
  form,
  path,
}: {
  disabled: boolean;
  form: BusinessFormApi;
  path: string;
}) => {
  return (
    <>
      <AddressTextField
        disabled={disabled}
        form={form}
        name={`${path}.name`}
        placeholder="Address line 1"
        required
      />
      <AddressTextField
        disabled={disabled}
        form={form}
        name={`${path}.street`}
        placeholder="Address line 2 (optional)"
      />
      <AddressTextField
        disabled={disabled}
        form={form}
        name={`${path}.city`}
        placeholder="City"
        required
      />
      <AddressTextField
        disabled={disabled}
        form={form}
        name={`${path}.region`}
        placeholder="Region"
        required
      />
      <TanStackPostalCodeField
        form={form}
        name={fieldName(`${path}.postalCode`)}
        countryField={fieldName(`${path}.country`)}
        label="Postal code"
        disabled={disabled}
        errorClassName={invalidFieldClassName}
      />
      <form.Field
        name={fieldName(`${path}.country`)}
        validators={requiredField("Country")}
      >
        {({ state, handleChange, handleBlur }) => {
          const errorMessage = getFieldError(state.meta.errors);

          return (
            <InputField
              label="Country"
              className={errorMessage ? invalidFieldClassName : undefined}
              note={fieldNote(undefined, errorMessage)}
              containerClassName={
                errorMessage
                  ? "border-red-300 focus-within:border-red-500"
                  : undefined
              }
            >
              <InputSelect
                name={`${path}.country`}
                value={String(state.value ?? "")}
                onBlur={handleBlur}
                onChange={(event) =>
                  handleChange(event.target.value as CountryType)
                }
                disabled={disabled}
                required
              >
                {Object.keys(CountryCodeDictionary).map((key) => (
                  <InputOption key={key} value={key}>
                    {
                      CountryCodeDictionary[
                        key as keyof typeof CountryCodeDictionary
                      ]
                    }
                  </InputOption>
                ))}
              </InputSelect>
            </InputField>
          );
        }}
      </form.Field>
    </>
  );
};

const AddressTextField = ({
  disabled,
  form,
  name,
  placeholder,
  required = false,
}: {
  disabled: boolean;
  form: BusinessFormApi;
  name: string;
  placeholder: string;
  required?: boolean;
}) => (
  <form.Field
    name={fieldName(name)}
    validators={required ? requiredField(placeholder) : undefined}
  >
    {({ state, handleChange, handleBlur }) => {
      const errorMessage = getFieldError(state.meta.errors);

      return (
        <InputField
          label={placeholder}
          className={errorMessage ? invalidFieldClassName : undefined}
          note={fieldNote(undefined, errorMessage)}
          containerClassName={
            errorMessage
              ? "border-red-300 focus-within:border-red-500"
              : undefined
          }
        >
          <InputText
            name={name}
            value={String(state.value ?? "")}
            onBlur={handleBlur}
            onChange={(event) => handleChange(event.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
          />
        </InputField>
      );
    }}
  </form.Field>
);

const FieldGroup = ({
  label,
  note,
  children,
}: {
  label: string;
  note?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-4 border-t border-solid border-zinc-200 pt-5 first:border-t-0 first:pt-0">
    <H4 className="text-zinc-900 dark:text-zinc-100">{label}</H4>
    {note && <H6>{note}</H6>}
    {children}
  </div>
);

const SwitchRow = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-10 items-center gap-2 text-sm text-zinc-800">
    {children}
  </div>
);
